import React, { useState, useMemo } from 'react';
import { Package, User, Search, X } from 'lucide-react';

function OrdersList({ orders, onItemClick }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter orders based on search term
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;

    const search = searchTerm.toLowerCase().trim();
    
    return orders.filter((order) => {
      // Search by ID
      if (order.id.toString().includes(search)) return true;
      
      // Search by customer name
      if (order.customer_name?.toLowerCase().includes(search)) return true;
      
      // Search by phone (telefono)
      if (order.telefono?.includes(search)) return true;
      
      // Search by driver ID
      if (order.driver_id && order.driver_id.toString().includes(search)) return true;
      
      return false;
    });
  }, [orders, searchTerm]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      encamino: 'bg-blue-100 text-blue-800 border-blue-300',
      completado: 'bg-green-100 text-green-800 border-green-300',
      cancelado: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En camino',
      completado: 'Completado',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  };

  const truncateName = (name, maxLength = 15) => {
    if (!name) return 'Sin nombre';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white px-3 py-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800 font-montserrat">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#E6B43C]/15">
              <Package className="w-4 h-4 text-[#B1336E]" />
            </span>
            Recojos ({orders.length})
          </h2>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white py-2 pl-10 pr-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#B1336E] font-montserrat"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Results count when searching */}
        {searchTerm && (
          <p className="mt-2 text-xs text-slate-500 font-montserrat">
            {filteredOrders.length} resultado{filteredOrders.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <Package className="w-12 h-12 text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm font-montserrat">
              {searchTerm 
                ? 'No se encontraron recojos con ese criterio'
                : 'No hay recojos para esta fecha'
              }
            </p>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="mt-3 text-[#B1336E] hover:text-[#B1336E]/80 text-sm font-medium font-montserrat"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 p-2">
            {filteredOrders.map((order) => (
              <button
                key={order.id}
                onClick={() => onItemClick(order.id)}
                className="w-full rounded-md border border-slate-200 bg-white p-3 text-left transition-all hover:border-[#B1336E]/30 hover:bg-slate-50 hover:shadow-sm"
              >
                {/* ID and Status */}
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-[#B1336E] font-bold text-sm font-montserrat">
                      #{order.id}
                    </span>
                    {order.sequence && (
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700 font-montserrat">
                        Seq: {order.sequence}
                      </span>
                    )}
                  </div>
                  <span
                    className={`flex-shrink-0 whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium font-montserrat ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {getStatusLabel(order.status)}
                  </span>
                </div>

                {/* Customer Name */}
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="min-w-0 truncate text-sm font-medium text-slate-800 font-montserrat">
                    {truncateName(order.customer_name)}
                  </span>
                </div>

                {/* Phone */}
                {order.telefono && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-slate-600 text-xs font-montserrat">
                      {order.telefono}
                    </span>
                  </div>
                )}

                {/* Driver Info */}
                {order.driver_id && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500 font-montserrat">
                      Conductor: ID {order.driver_id}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrdersList;
